import React, { useEffect, useState } from 'react';
import { Button, Container, Dropdown } from 'react-bootstrap';
import { del, get } from 'aws-amplify/api';
import WordlistCard from './WordlistCard';
import AboutWordlistsModal from './AboutWordlistsModal';
import { FaCircleInfo, FaCirclePlus, FaFileArrowUp } from 'react-icons/fa6';
import UploadNewWordlistModal from './UploadNewWordlistModal';
import { useToastNotifications } from '../../libs/toastLib';
import WordlistLoadingAnimation from './WordlistLoadingAnimation';
import { GrDocumentTxt } from 'react-icons/gr';
import { getCurrentWordlistObj, tryToDownloadBaseWordlistFile, useSwdContext } from '../../libs/autofillLib';
import LoaderButton from '../reusable/LoaderButton';
import { userFriendlyDateString } from '../../libs/miscLib';
import EditWordlistModal from './EditWordlistModal';
import { TbFileSearch } from 'react-icons/tb';
import { useAppContext } from '../../App';



function BaseWordlistItem({ baseWordlist, triggerRefresh }) {

  // Keep track of currently selected wordlist, based on the swdVersion
  const { swdVersion } = useSwdContext();
  const [activeWordlist, setActiveWordlist] = useState(null);
  useEffect(() => {
    setActiveWordlist(getCurrentWordlistObj());
  }, [swdVersion]);

  // Regular state
  const isActive = activeWordlist && activeWordlist.baseWordlistId === baseWordlist?.baseWordlistId;

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <div className='row align-items-center mb-2' key={baseWordlist.baseWordlistId}>
      <div className='col d-flex align-items-center text-success gap-1'>
        <GrDocumentTxt size={17} />
        <div className='text-xs ms-1'>
          <div>{baseWordlist.filename}</div>
          <div className='text-muted text-xs'>
            {baseWordlist.stats?.fileSizeMB?.toFixed(1) || '?'} MB  &middot;  {userFriendlyDateString(baseWordlist.createdAt)}
          </div>
        </div>
      </div>

      <div className='col-auto'>
        <LoaderButton
          className='py-0'
          isLoading={isDownloading}
          variant='outline-secondary'
          size='sm'
          onClick={async () => {
            setIsDownloading(true);
            await tryToDownloadBaseWordlistFile(baseWordlist.baseWordlistId, baseWordlist.filename);
            setIsDownloading(false);
          }}
        >Download</LoaderButton>
      </div>

      {baseWordlist.permissionsLevel === 'MANAGE' && (
        <div className='col-auto'>
          <LoaderButton
            className='py-0'
            variant='outline-danger'
            size='sm'
            isLoading={isDeleting}
            disabled={isActive}
            onClick={async () => {
              if (!window.confirm('Are you sure you want to delete this file? Any wordlists based on this file will no longer be available.')) return;
              setIsDeleting(true);
              try {
                await del({
                  apiName: 'wordlists',
                  path: `/baseWordlists/${baseWordlist.baseWordlistId}`
                }).response;
              } catch (e) {
                console.log(e);
              }
              setIsDeleting(false);
              if (triggerRefresh) triggerRefresh();
            }}
          >Delete</LoaderButton>
        </div>
      )}
    </div>
  );
}



export default function Wordlists() {

  const [manualRefreshTrigger, setManualRefreshTrigger] = useState(0);   // increment to force reload of wordlists
  const triggerRefresh = () => {
    setManualRefreshTrigger(s => s + 1);
  }

  // Load scoredWordDatabase if it doesn't exist yet
  const { requireWordlist, hasLoadedFirstWordlist } = useSwdContext();
  requireWordlist();   // special function to indicate to SwdContext that this component wants the wordlist loaded

  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const [wordlists, setWordlists] = useState(null);
  const [baseWordlists, setBaseWordlists] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { postErrorNotification, removeNotificationsWithLabels } = useToastNotifications();
  const { fetchDifferentWordlist } = useSwdContext();
  const { getUserPreference } = useAppContext();

  useEffect(() => {
    async function fetchWordlists() {
      setIsLoading(true);
      // Load all available wordlists, and baseWordlists
      try {
        removeNotificationsWithLabels(['load-wordlist-error']);
        const [w, bw, activeWordlistId] = await Promise.all([
          get({ apiName: 'wordlists', path: '/wordlists' }).response.then(r => r.body.json()),
          get({ apiName: 'wordlists', path: '/baseWordlists' }).response.then(r => r.body.json()),
          // get({ apiName: 'userPreferences', path: '/activeWordlistId' }).response.then(r => r.body.json()),
          getUserPreference('activeWordlistId'),
        ]);
        setWordlists(w);
        setBaseWordlists(bw);

        // Since there may have been edits to the active wordlist, or the active wordlist may have changed, also need to re-fetch and index that
        const newActiveWordlistObj = w.find(o => o.wordlistId === activeWordlistId);
        fetchDifferentWordlist(newActiveWordlistObj);
      } catch (e) {
        console.error(e);
        postErrorNotification('Unable to load wordlists', 'Try again later; if this issue persists, please let us know!', ['load-wordlist-error']);
      }
      setIsLoading(false);
    }
    fetchWordlists();
  }, [manualRefreshTrigger, postErrorNotification, removeNotificationsWithLabels, fetchDifferentWordlist, getUserPreference]);

  return (
    <Container className='my-5'>
      <section className='border-bottom mb-3'>
        <div className='h2 mb-1'>My Wordlists</div>
        <div>Create, customize, and collect wordlists to help optimize your crossword fills.</div>
      </section>

      <section className='border-bottom mb-4'>
        <div className='d-flex'>
          <Dropdown>
            <Dropdown.Toggle className='d-flex align-items-center gap-2' variant='info'>
              <FaCirclePlus />
              Add new wordlist
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setShowUploadModal(true)}><FaFileArrowUp /> New file upload</Dropdown.Item>
              <Dropdown.Item onClick={() => setShowAddModal(true)}><TbFileSearch /> From existing</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Button className='ms-auto d-flex align-items-center gap-2' variant='outline-primary' onClick={() => setShowAboutModal(true)}>
            <FaCircleInfo />
            How wordlists work on Crossworthy
          </Button>
        </div>

        {isLoading ? (
          <div className='py-4 d-flex flex-column gap-4 align-items-center'>
            <WordlistLoadingAnimation />
            <div className='block-text lg'>Collecting my files...</div>
          </div>
        ) : !hasLoadedFirstWordlist? (
          <div className='py-4 d-flex flex-column gap-4 align-items-center'>
            <WordlistLoadingAnimation />
            <div className='block-text lg'>Indexing my active wordlist...</div>
          </div>
        ) : (
          <div className='my-3 pt-3 d-flex flex-wrap gap-3 justify-content-start'>
            {wordlists?.toSorted((a, b) => (b.usedAt || b.modifiedAt) - (a.usedAt || a.modifiedAt))?.map(wordlist => (
              <WordlistCard key={wordlist.wordlistId} wordlist={wordlist} triggerRefresh={triggerRefresh} />
            ))}
          </div>
        )}
      </section>

      {baseWordlists?.some(item => !item.isCommunity) && (
        <section>
          <div className='fw-bold'>Uploaded files</div>
          <div className='small text-muted'>
            This is a list of all the raw files you've uploaded.
          </div>

          {isLoading ? (
            <div className='fst-italic'>Loading...</div>
          ) : (
            <div className='my-3 not-too-wide'>
              {baseWordlists?.filter(item => !item.isCommunity)?.toSorted((a, b) => b.createdAt - a.createdAt)?.map(baseWordlist => (
                <BaseWordlistItem key={baseWordlist.baseWordlistId} baseWordlist={baseWordlist} triggerRefresh={triggerRefresh} />
              ))}
            </div>
          )}
        </section>
      )}

      <AboutWordlistsModal show={showAboutModal} onHide={() => setShowAboutModal(false)} />
      <UploadNewWordlistModal show={showUploadModal} onHide={() => setShowUploadModal(false)} onSuccess={() => triggerRefresh()} />
      <EditWordlistModal show={showAddModal} onHide={() => setShowAddModal(false)} triggerRefresh={triggerRefresh} createNew={true} fromActive={false} />
    </Container>
  );
}